import React, { useState, useEffect, useRef } from 'react';
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import './index.css';

export default function ProductCarousel({ images, baseUrl }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pointer, setPointer] = useState({ x: "50%", y: "50%" });
  const [display, setDisplay] = useState("none");
  const [startIndex, setStartIndex] = useState(0);
  const [isTouch, setIsTouch] = useState(false);
  const [touchStartTime, setTouchStartTime] = useState(0);
  const thumbnailsRef = useRef(null);
  const zoomTimeoutRef = useRef(null);

  const getZoomLevel = () => {
    const img = document.querySelector(`img[src="${baseUrl}${images[currentIndex]}"]`);
    if (!img || !img.dataset.naturalWidth) return 150; // Default zoom level
    
    // Get the displayed size vs natural size ratio
    const displayWidth = img.width;
    const naturalWidth = parseInt(img.dataset.naturalWidth);
    const ratio = naturalWidth / displayWidth;
    
    // Calculate appropriate zoom based on ratio
    // Higher ratio (smaller displayed image relative to natural size) = higher zoom
    if (ratio > 3) return 200; // Very large image, can zoom more
    if (ratio > 2) return 175; // Large image
    if (ratio > 1) return 150; // Standard image
    return 125; // Small image that's being stretched, use less zoom
  }

  // Calculate the number of visible thumbnails (5 at most)
  const visibleThumbnails = images ? Math.min(5, images.length) : 0;
  
  // Ensure current image is within visible range when it changes
  useEffect(() => {
    if (currentIndex < startIndex) {
      setStartIndex(currentIndex);
    } else if (currentIndex >= startIndex + visibleThumbnails) {
      setStartIndex(currentIndex - visibleThumbnails + 1);
    }
  }, [currentIndex, startIndex, visibleThumbnails]);

  // Handle mouse move for desktop
  const handleMouseMove = (e) => {
    if (isTouch) return; // Don't handle mouse events if touch is active
    
    const { offsetX, offsetY, target } = e.nativeEvent;

    // Calculate percentage position inside the element
    const x = (offsetX / target.offsetWidth) * 100;
    const y = (offsetY / target.offsetHeight) * 100;

    setPointer({ x: `${x}%`, y: `${y}%` });
    setDisplay("block");
  };

  // Handle mouse leave for desktop
  const handleMouseLeave = () => {
    if (isTouch) return; // Don't handle mouse events if touch is active
    setDisplay("none");
  };

  // Handle touch start
  const handleTouchStart = (e) => {
    setIsTouch(true);
    setTouchStartTime(Date.now());
    
    // Get touch position
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    
    setPointer({ x: `${x}%`, y: `${y}%` });
    setDisplay("block");
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent scrolling while zooming
    
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    
    setPointer({ x: `${x}%`, y: `${y}%` });
    setDisplay("block");
  };

  // Handle touch end
  const handleTouchEnd = (e) => {
    const touchDuration = Date.now() - touchStartTime;
    
    // If it's a quick tap (less than 300ms), hide zoom after a short delay
    if (touchDuration < 300) {
      // Clear any existing timeout
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
      
      // Set a timeout to hide zoom after 2 seconds
      zoomTimeoutRef.current = setTimeout(() => {
        setDisplay("none");
        setIsTouch(false);
      }, 2000);
    } else {
      // If it's a longer touch, hide zoom immediately
      setDisplay("none");
      setIsTouch(false);
    }
  };

  // Clear timeout when component unmounts
  useEffect(() => {
    return () => {
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
    };
  }, []);

  const goToImage = (index) => setCurrentIndex(index);

  const goToPrevious = () =>
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));

  const goToNext = () =>
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  
  if (!images || images.length === 0) return null;

  // Create the visible thumbnails array
  const visibleImages = images.slice(startIndex, startIndex + visibleThumbnails);
  const hasMorePrev = images.length > visibleThumbnails;
  const hasMoreNext = images.length > visibleThumbnails;

  return (
    <div className="w-full pt-0 max-w-3xl mx-auto">
      <div className="relative">
        {/* Main Image with Zoom Effect */}
        <div
          className="box relative w-full h-96 bg-gradient-to-r from-[#E0F0EB] to-[#E1EDF2] rounded-lg mb-4 overflow-hidden cursor-zoom-in touch-none"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            touchAction: 'none' // Prevents default touch behaviors like scrolling
          }}
        >
          {/* Main image - will be hidden when zoom is active */}
          <div className="w-full h-full flex justify-center items-center">
            <img
              src={`${baseUrl}${images[currentIndex]}`}
              alt={`Product ${currentIndex + 1}`}
              className="w-[200px] h-[200px] md:w-[250px] md:h-[250px] 2xl:w-[300px] 2xl:h-[300px] object-cover select-none"
              style={{
                opacity: display === "none" ? 1 : 0,
                transition: "opacity 0.2s ease"
              }}
              onLoad={(e) => {
                // Store the natural dimensions of the image for better zoom calculations
                const img = e.target;
                img.dataset.naturalWidth = img.naturalWidth;
                img.dataset.naturalHeight = img.naturalHeight;
              }}
              draggable={false}
            />
          </div>
          
          {/* Zoom overlay */}
          <div
            className="absolute inset-0 pointer-events-none select-none"
            style={{
              backgroundImage: `url('${baseUrl}${images[currentIndex]}')`,
              backgroundSize: `${Math.max(140, getZoomLevel())}%`, // Dynamic zoom level based on image size
              backgroundPosition: `${pointer.x} ${pointer.y}`,
              display: display,
              opacity: 1,
              transition: isTouch ? 'none' : 'background-position 0.1s ease'
            }}
          />
          
          {/* Touch indicator for mobile */}
          {isTouch && display === "block" && (
            <div 
              className="absolute pointer-events-none z-20 w-4 h-4 bg-white border-2 border-blue-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 opacity-70 md:hidden"
              style={{
                left: pointer.x,
                top: pointer.y,
                transition: 'none'
              }}
            />
          )}
          
          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 z-10 touch-manipulation"
            style={{ touchAction: 'manipulation' }}
          >
            <FaChevronLeft className="w-6 h-6" />
          </button>
          
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 z-10 touch-manipulation"
            style={{ touchAction: 'manipulation' }}
          >
            <FaChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Thumbnail Navigation */}
        <div className="relative flex justify-center mt-4 mb-2">
          {/* Left thumbnail navigation button */}
          {hasMorePrev && (
            <button 
              onClick={goToPrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md z-10 touch-manipulation"
              aria-label="Previous thumbnails"
              style={{ touchAction: 'manipulation' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
          )}
          
          {/* Fixed-width container showing exactly 5 thumbnails (or fewer if not enough images) */}
          <div 
            ref={thumbnailsRef}
            className="flex space-x-4 justify-center"
          >
            {visibleImages.map((image, index) => {
              const actualIndex = startIndex + index;
              return (
                <button
                  key={actualIndex}
                  onClick={() => goToImage(actualIndex)}
                  className={`w-24 h-24 border-2 rounded-lg overflow-hidden transition-all duration-200 flex-shrink-0 touch-manipulation ${
                    currentIndex === actualIndex ? "border-blue-500" : "border-gray-200"
                  }`}
                  style={{ touchAction: 'manipulation' }}
                >
                  <img
                    src={`${baseUrl}${image}`}
                    alt={`Thumbnail ${actualIndex + 1}`}
                    className="w-full h-full object-contain select-none"
                    draggable={false}
                  />
                </button>
              );
            })}
          </div>
          
          {/* Right thumbnail navigation button */}
          {hasMoreNext && (
            <button 
              onClick={goToNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md z-10 touch-manipulation"
              aria-label="Next thumbnails"
              style={{ touchAction: 'manipulation' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}